import { z, isoZ } from 'my-zod'
import { dateFns } from 'iso-fns2'
import { productLineOptions } from '../../internal'

export const outfitterDocumentTypeEnum = z
  .enum([
    'BusinessPlan',
    'CertificateOfInsurance',
    'ExtensionAddendum',
    'FleetVehicleOrder',
    'InsuranceCard',
    'InsuranceNotices',
    'LetterOfIntent',
    'MSO',
    'NonDisclosureAgreement',
    'ParkingTollViolation',
    'PowerOfAttorney',
    'ProgramAgreement',
    'SalesExemptionForm',
    'TaxForm',
    'TerminationLetter',
    'TitleApplicationBlank',
    'TitleApplicationExample',
    'Other'
  ])
  .setEnumLabels({
    BusinessPlan: 'Business Plan',
    CertificateOfInsurance: 'Certificate of Insurance',
    ExtensionAddendum: 'Extension Addendum',
    FleetVehicleOrder: 'Fleet Vehicle Order',
    InsuranceNotices: 'Insurance Notices',
    InsuranceCard: 'Insurance Card',
    LetterOfIntent: 'Letter of Intent',
    MSO: 'MSO',
    NonDisclosureAgreement: 'Non-Disclosure Agreement',
    ParkingTollViolation: 'Parking/Toll Violation',
    PowerOfAttorney: 'Power of Attorney',
    ProgramAgreement: 'Program Agreement',
    SalesExemptionForm: 'Sales Exemption Form',
    TaxForm: 'Tax Form',
    TerminationLetter: 'Termination Letter',
    TitleApplicationBlank: 'Title Application (Blank)',
    TitleApplicationExample: 'Title Application (Example)',
    Other: 'Other'
  })

export type outfitterDocumentTypeEnum = z.infer<typeof outfitterDocumentTypeEnum>

export const outfitterDocumentStatusEnum = z
  .enum(['Active', 'Void', 'Terminated', 'ReviewRequired', 'Expired'])
  .setEnumLabels({
    Active: 'Active',
    Void: 'Void',
    Terminated: 'Terminated',
    ReviewRequired: 'Review Required',
    Expired: 'Expired'
  })

export type outfitterDocumentStatusEnum = z.infer<typeof outfitterDocumentStatusEnum>

export const outfitterDocumentCurrencyEnum = z.enum(['USD', 'CAD'])

export const outfitterDocumentModel = z
  .object({
    shortId: z.string(),
    documentType: outfitterDocumentTypeEnum.setLabel('Document Type'),
    lifecycleStatus: outfitterDocumentStatusEnum.nullable().setLabel('Status'),
    currency: outfitterDocumentCurrencyEnum.nullable().setLabel('Currency'),
    productLines: z.enum(productLineOptions).array(),
    outfitterId: z.number().min(1, 'Required').setLabel('Outfitter Name'),
    contactId: z.string().uuid().nullable().setLabel('Contact Name'),
    startDate: isoZ.date().nullable(),
    endDate: isoZ.date().nullable(),
    inactiveDate: isoZ.date().nullable(),
    sharedWithOutfitterDate: isoZ.date().nullable(),
    recipients: z.string().array().nullable().setLabel('Email Recipients'),
    notes: z.string().nullable(),
    documentDescription: z.string().max(36).min(1).nullable(),
    createdUserId: z.number().setLabel('Created By'),
    lastModifiedByUserId: z.number().setLabel('Last Modified By'),
    createdDate: isoZ.instant(),
    deletedDate: isoZ.instant().nullable(),
    lastModifiedDate: isoZ.instant()
  })
  .setCFV({
    productLinesRequired(val) {
      if (!val.productLines?.length && val.documentType === outfitterDocumentTypeEnum.Enum.ProgramAgreement) {
        return {
          message: 'Required',
          path: ['productLines']
        }
      }
    },
    startDateRequired(val) {
      if (
        !val.startDate &&
        [
          outfitterDocumentTypeEnum.Enum.CertificateOfInsurance,
          outfitterDocumentTypeEnum.Enum.ExtensionAddendum,
          outfitterDocumentTypeEnum.Enum.ProgramAgreement,
          outfitterDocumentTypeEnum.Enum.PowerOfAttorney,
          outfitterDocumentTypeEnum.Enum.TerminationLetter,
          outfitterDocumentTypeEnum.Enum.NonDisclosureAgreement
        ].includes(val.documentType as any)
      ) {
        return {
          message: 'Required',
          path: ['startDate']
        }
      }
    },
    endDateRequired(val) {
      if (
        !val.endDate &&
        [
          outfitterDocumentTypeEnum.Enum.NonDisclosureAgreement,
          outfitterDocumentTypeEnum.Enum.CertificateOfInsurance,
          outfitterDocumentTypeEnum.Enum.ExtensionAddendum,
          outfitterDocumentTypeEnum.Enum.ProgramAgreement,
          outfitterDocumentTypeEnum.Enum.PowerOfAttorney
        ].includes(val.documentType as any)
      ) {
        return {
          message: 'Required',
          path: ['endDate']
        }
      }
    },
    inactiveDateRequired(val) {
      if (
        !val.inactiveDate &&
        [outfitterDocumentStatusEnum.Enum.Void, outfitterDocumentStatusEnum.Enum.Terminated].includes(
          val.lifecycleStatus as any
        )
      ) {
        return {
          message: 'Required',
          path: ['inactiveDate']
        }
      }
    },
    datesValidation(val) {
      if (val.startDate && val.endDate && dateFns.isAfter(val.startDate, val.endDate)) {
        return {
          message: 'Should be equal or before End Date',
          path: ['startDate']
        }
      }
      if (val.startDate && val.inactiveDate && dateFns.isBefore(val.inactiveDate, val.startDate)) {
        return {
          message: 'Should be equal or after Start Date',
          path: ['inactiveDate']
        }
      }
      if (val.endDate && val.inactiveDate && dateFns.isAfter(val.inactiveDate, val.endDate)) {
        return {
          message: 'Should be equal or before End Date',
          path: ['inactiveDate']
        }
      }
    },
    descriptionRequired(val) {
      if (!val.documentDescription && [outfitterDocumentTypeEnum.Enum.Other].includes(val.documentType as any)) {
        return {
          message: 'Required',
          path: ['documentDescription']
        }
      }
    }
  })

export const allowedDocumentTypesForHQ = [
  'InsuranceCard',
  'TaxForm',
  'TitleApplicationBlank',
  'TitleApplicationExample',
  'MSO',
  'SalesExemptionForm',
  'Other'
]
